/**
 * bridge-commands.js — the server-driven command layer over window.ChippieBridge.
 *
 * The point of a bridge component library is that native behaviour becomes
 * SERVER-CONFIGURABLE: changing when the phone buzzes, what the status bar does,
 * or when a review prompt appears should be a template/response change, not an
 * App Store submission. The facade (chippie-bridge.js) makes a capability
 * callable; this file makes it callable BY THE SERVER, three ways:
 *
 *   1. Declaratively, from rendered markup — data-bridge-call on any element,
 *      driven by the bridge--invoke Stimulus controller, which calls invoke().
 *   2. In a Turbo Stream response — <turbo-stream action="bridge" call="haptic">,
 *      so any form POST can fire native feedback with zero bespoke JS.
 *   3. Over Mercure, unprompted — a 'bridge_command' event (realtime-hub.js),
 *      so the server can reach a device with no request in flight.
 *
 * THE GATE (read before adding a name to REMOTE_ALLOWED)
 * Paths 2 and 3 are not user gestures, so they go through remote() and are
 * limited to REMOTE_ALLOWED below. Two independent reasons, and only the second
 * is negotiable:
 *   - The platforms REQUIRE a gesture for camera, geolocation, contacts, NFC and
 *     biometrics. A server-pushed call would be denied or, worse, pop a
 *     permission dialog the user cannot connect to anything they did. That is a
 *     lost grant, and on iOS a denied grant is sticky.
 *   - Abuse surface. A capability reachable from a push is reachable by anything
 *     that can publish a push.
 *
 * What this gate is NOT: an XSS mitigation. Anything that can inject a
 * <turbo-stream> into the page can equally call window.ChippieBridge.location()
 * directly. It is a design guardrail that encodes platform policy.
 *
 * REMOTE_ALLOWED is duplicated as BRIDGE_REMOTE_CALLS in application/sse/events.py,
 * on purpose: this list refuses at CALL time (the only gate a <turbo-stream> from
 * any other source has to pass), that one refuses at PUBLISH time (so the mistake
 * is a stack trace in dev, not a console warning on a user's phone). Extend BOTH —
 * tests/unit/test_bridge_remote_allowlist.py fails the build if they drift.
 * Rationale: docs/plans/decisions/012-server-driven-bridge-invocation.md
 *
 * Load order: after chippie-bridge.js (needs the facade), before the controllers
 * bundle (bridge--invoke calls into it). Both shells' <head>, deferred.
 *
 * <!-- JavaScript: static/js/chippie-bridge.js (the facade this extends) -->
 */
(function () {
    "use strict";

    if (!window.ChippieBridge || window.ChippieBridge.invoke) return;

    /**
     * Capabilities the SERVER may fire without a user gesture.
     *
     * Permission-free, non-modal, and individually harmless if repeated. A
     * namespaced capability is listed by its full dotted path so one method can
     * be allowed without its siblings.
     *
     * Deliberately absent, and why:
     *   location, scan, readNfc, contactPicker, documentScanner, biometricsLock
     *                        -> gesture-required by the OS; see THE GATE above.
     *   localNotification.*  -> the Android impl requests POST_NOTIFICATIONS on
     *                           first use, so a push could raise a permission
     *                           dialog out of nowhere.
     *   share, print         -> take over the screen with a system sheet.
     *   calendarEvent        -> writes to the user's calendar.
     *   secureStorage.*      -> reads/writes credentials.
     */
    var REMOTE_ALLOWED = [
        "haptic",
        "toast",
        "theme",
        "screenBrightness",
        "keepAwake",
        "requestReview",
        "openSettings",
        "appInfo",
        "appLifecycle.state",
        "networkStatus",
    ];

    /**
     * Resolve a dotted capability path against the facade.
     * "haptic" -> ChippieBridge.haptic; "localNotification.schedule" -> that method.
     * Returns null (never throws) when the capability is not registered — an
     * unmounted or not-yet-built component must degrade, not break the caller.
     */
    function resolve(path) {
        var parts = String(path || "").split(".");
        var ctx = window.ChippieBridge;
        for (var i = 0; i < parts.length; i++) {
            if (!ctx) return null;
            ctx = ctx[parts[i]];
        }
        return typeof ctx === "function" ? ctx : null;
    }

    /**
     * Call a capability with an argument array. This is the USER-GESTURE path:
     * no whitelist, because the user did something to cause it.
     * Returns whatever the impl returns (often a Promise), or undefined if the
     * capability is unavailable in this runtime.
     */
    function invoke(path, args) {
        var fn = resolve(path);
        if (!fn) {
            console.warn("[bridge] no such capability:", path);
            return undefined;
        }
        try {
            return fn.apply(null, Array.isArray(args) ? args : []);
        } catch (e) {
            // A bridge gap must never break the page that triggered it.
            console.warn("[bridge] invoke failed:", path, e);
            return undefined;
        }
    }

    /**
     * Call a capability on behalf of the SERVER, with no user gesture behind it.
     * Drops anything outside REMOTE_ALLOWED, loudly enough to debug but without
     * throwing — a rejected command is a config mistake, not a page error.
     */
    function remote(path, args) {
        if (REMOTE_ALLOWED.indexOf(String(path)) === -1) {
            console.warn(
                "[bridge] refused server-driven call to '" + path +
                "' — not in REMOTE_ALLOWED (bridge-commands.js). " +
                "Trigger it from a user gesture with data-bridge-call instead."
            );
            return undefined;
        }
        return invoke(path, args);
    }

    window.ChippieBridge.invoke = invoke;
    window.ChippieBridge.remote = remote;
    // Exposed so the catalogue check in native/BRIDGE_COMPONENTS.md and console
    // debugging can read the gate without re-deriving it.
    window.ChippieBridge.remoteAllowed = REMOTE_ALLOWED.slice();

    /**
     * Turbo Stream action: <turbo-stream action="bridge" call="haptic" args='["success"]'>
     *
     * No target/template needed — this touches the device, not the DOM. Turbo
     * still requires the element to be well formed, so `args` rides as an
     * attribute (JSON array) rather than a template body.
     *
     * Registered the same way turbo-compat.js registers append_option: try now,
     * fall back to the first turbo:load if Turbo has not booted yet.
     */
    function registerStreamAction() {
        if (!window.Turbo || !window.Turbo.StreamActions || window.Turbo.StreamActions.bridge) {
            return false;
        }
        window.Turbo.StreamActions.bridge = function () {
            var call = this.getAttribute("call");
            var args = [];
            var raw = this.getAttribute("args");
            if (raw) {
                try { args = JSON.parse(raw); } catch (_e) {
                    console.warn("[bridge] turbo-stream args is not valid JSON:", raw);
                    return;
                }
            }
            remote(call, args);
        };
        return true;
    }

    if (!registerStreamAction()) {
        document.addEventListener("turbo:load", registerStreamAction, { once: true });
    }
})();
